import React from "react";
import { Check } from "lucide-react";
import { t } from "@/i18n/translations";

const PipelineIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-6 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Wildlife camera pipeline diagram">
      <defs>
        <marker id="arrowL" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L8,5 L0,10 z" fill="#242220" />
        </marker>
        <linearGradient id="dotGlow" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#FBD556" />
          <stop offset="100%" stopColor="#FF9456" />
        </linearGradient>
      </defs>
      <path d="M86 180 H134" stroke="#242220" strokeWidth="2" markerEnd="url(#arrowL)" />
      <path d="M186 180 H234" stroke="#242220" strokeWidth="2" markerEnd="url(#arrowL)" />
      <path d="M286 180 H334" stroke="#242220" strokeWidth="2" markerEnd="url(#arrowL)" />
      <path d="M386 180 H422" stroke="#242220" strokeWidth="2" markerEnd="url(#arrowL)" />
      <g transform="translate(36 140)">
        <rect width="50" height="80" rx="10" fill="#FBD556" stroke="#242220" strokeWidth="1.5" />
        <circle cx="25" cy="32" r="12" fill="#1E1E1E" stroke="#242220" strokeWidth="1.5" />
        <circle cx="25" cy="32" r="6" fill="#A1E1B9" />
        <rect x="10" y="58" width="30" height="6" rx="2" fill="#242220" />
        <text x="25" y="96" textAnchor="middle" fontSize="11" fontWeight="700" fill="#242220" fontFamily="Quicksand">Camera</text>
      </g>
      <g transform="translate(134 144)">
        <rect width="52" height="36" rx="6" fill="#FFFFFF" stroke="#242220" strokeWidth="1.5" />
        <path d="M0 4 L26 22 L52 4" stroke="#242220" strokeWidth="1.5" fill="none" />
        <text x="26" y="64" textAnchor="middle" fontSize="11" fontWeight="700" fill="#242220" fontFamily="Quicksand">SYNC</text>
      </g>
      <g transform="translate(234 142)">
        <rect width="52" height="44" rx="6" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <rect x="6" y="8" width="40" height="6" rx="2" fill="#242220" />
        <rect x="6" y="18" width="40" height="6" rx="2" fill="#242220" />
        <rect x="6" y="28" width="40" height="6" rx="2" fill="#242220" />
        <circle cx="44" cy="11" r="1.5" fill="#A1E1B9" />
        <text x="26" y="64" textAnchor="middle" fontSize="11" fontWeight="700" fill="#242220" fontFamily="Quicksand">Rust API</text>
      </g>
      <g transform="translate(334 142)">
        <circle cx="26" cy="22" r="22" fill="#A1E1B9" stroke="#242220" strokeWidth="1.5" />
        <ellipse cx="26" cy="22" rx="22" ry="8" stroke="#242220" strokeWidth="1.2" fill="none" />
        <path d="M26 0 V44" stroke="#242220" strokeWidth="1.2" />
        <path d="M14 8 Q 26 18, 38 8" stroke="#242220" strokeWidth="1.2" fill="none" />
        <text x="26" y="64" textAnchor="middle" fontSize="11" fontWeight="700" fill="#242220" fontFamily="Quicksand">Bunny CDN</text>
      </g>
      <g transform="translate(422 138)">
        <ellipse cx="20" cy="6" rx="20" ry="6" fill="#FBD556" stroke="#242220" strokeWidth="1.5" />
        <path d="M0 6 V42 a20 6 0 0 0 40 0 V6" fill="#FBD556" stroke="#242220" strokeWidth="1.5" />
        <ellipse cx="20" cy="20" rx="20" ry="6" fill="none" stroke="#242220" strokeWidth="1.2" />
        <ellipse cx="20" cy="32" rx="20" ry="6" fill="none" stroke="#242220" strokeWidth="1.2" />
        <text x="20" y="68" textAnchor="middle" fontSize="11" fontWeight="700" fill="#242220" fontFamily="Quicksand">PostGIS</text>
      </g>
      <circle r="6" fill="url(#dotGlow)" stroke="#242220" strokeWidth="1.5">
        <animateMotion dur="5s" repeatCount="indefinite" path="M61,180 H134 L160,162 H186 L210,180 H234 L260,162 H286 L310,180 H334 L360,162 H386 L410,180 H442" />
      </circle>
      <text x="240" y="320" textAnchor="middle" fontSize="11" fill="#242220" fontFamily="Quicksand" fontWeight="600" opacity="0.55">
        motion → email → ingest → CDN → record
      </text>
    </svg>
  </div>
);

const BirdVoiceIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#1E1E1E] border-[1.5px] border-[#FDF5EB]/15 p-6 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Bird voice identification spectrogram">
      <g transform="translate(30 120)">
        {Array.from({ length: 18 }).map((_, i) => {
          const heights = [40, 80, 30, 120, 60, 100, 50, 140, 70, 110, 35, 90, 60, 130, 45, 100, 55, 70];
          const h = heights[i];
          return (
            <rect key={i} x={i * 9} y={(150 - h) / 2} width="5" height={h} rx="2.5" fill="#FBD556">
              <animate attributeName="height" values={`${h * 0.5};${h};${h * 0.7};${h};${h * 0.5}`} dur={`${1.6 + (i % 4) * 0.2}s`} repeatCount="indefinite" />
              <animate attributeName="y" values={`${(150 - h * 0.5) / 2};${(150 - h) / 2};${(150 - h * 0.7) / 2};${(150 - h) / 2};${(150 - h * 0.5) / 2}`} dur={`${1.6 + (i % 4) * 0.2}s`} repeatCount="indefinite" />
            </rect>
          );
        })}
      </g>
      <path d="M210 180 H260" stroke="#A1E1B9" strokeWidth="2.5" markerEnd="url(#arrBV)" />
      <defs>
        <marker id="arrBV" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L8,5 L0,10 z" fill="#A1E1B9" />
        </marker>
      </defs>
      <g transform="translate(280 120)">
        {Array.from({ length: 6 }).map((_, x) =>
          Array.from({ length: 4 }).map((_, y) => {
            const colors = ["#FBD556", "#A1E1B9", "#FF9456", "#FDF5EB"];
            const c = colors[(x + y) % 4];
            const op = ((x * y + 1) % 3) * 0.3 + 0.3;
            return (
              <rect key={`${x}-${y}`} x={x * 22} y={y * 22} width="18" height="18" rx="3" fill={c} opacity={op}>
                <animate attributeName="opacity" values={`${op};${op + 0.3};${op}`} dur={`${2 + ((x + y) % 3) * 0.4}s`} repeatCount="indefinite" />
              </rect>
            );
          })
        )}
      </g>
      <g transform="translate(140 270)">
        <rect width="200" height="36" rx="18" fill="#A1E1B9" stroke="#242220" strokeWidth="1.5" />
        <circle cx="20" cy="18" r="6" fill="#242220" />
        <path d="M16 18 L19 21 L24 14" stroke="#A1E1B9" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="36" y="23" fontSize="13" fontWeight="700" fontFamily="Quicksand" fill="#242220">
          Turdus merula · 94%
        </text>
      </g>
    </svg>
  </div>
);

const ChainIllustration = () => {
  const nodes = [
    { name: "Groq", sub: "Primary · ⚡", color: "#FBD556" },
    { name: "Gemini 2.5", sub: "Multimodal", color: "#FDF5EB" },
    { name: "OpenRouter", sub: "llama-3.1", color: "#FDF5EB" },
    { name: "Pl@ntNet", sub: "Botanical", color: "#A1E1B9" },
    { name: "Manual Review", sub: "Safety net", color: "#FF9456" },
  ];
  return (
    <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
      <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="AI model fallback chain">
        {nodes.map((n, i) => {
          const y = 22 + i * 62;
          return (
            <g key={n.name}>
              {i < nodes.length - 1 && (
                <path d={`M240 ${y + 50} V ${y + 72}`} stroke="#242220" strokeWidth="1.5" markerEnd="url(#arrCh)" strokeDasharray="3 3" />
              )}
              <rect x="80" y={y} width="320" height="48" rx="14" fill={n.color} stroke="#242220" strokeWidth="1.5" />
              <text x="100" y={y + 22} fontFamily="Quicksand" fontWeight="700" fontSize="14" fill="#242220">{`0${i + 1}`}</text>
              <line x1="124" y1={y + 12} x2="124" y2={y + 36} stroke="#242220" strokeWidth="1" opacity="0.3" />
              <text x="136" y={y + 22} fontFamily="Quicksand" fontWeight="700" fontSize="14" fill="#242220">{n.name}</text>
              <text x="136" y={y + 38} fontFamily="Quicksand" fontWeight="500" fontSize="11" fill="#242220" opacity="0.7">{n.sub}</text>
              {i === 0 && (
                <g transform={`translate(360 ${y + 14})`}>
                  <circle cx="10" cy="10" r="10" fill="#242220" />
                  <path d="M5 10 L9 14 L15 6" stroke="#A1E1B9" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </g>
              )}
            </g>
          );
        })}
        <defs>
          <marker id="arrCh" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto">
            <path d="M0,0 L8,5 L0,10 z" fill="#242220" />
          </marker>
        </defs>
        <circle r="5" fill="#FF9456" stroke="#242220" strokeWidth="1.5">
          <animateMotion dur="4.5s" repeatCount="indefinite" path="M240,46 V 108 V 170 V 232 V 294" keyPoints="0;0.2;0.4;0.6;0.8;1" keyTimes="0;0.2;0.4;0.6;0.8;1" />
        </circle>
      </svg>
    </div>
  );
};

const RoutingIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Ranger and hiker routing map">
      <path d="M40 80 L 130 50 L 220 90 L 200 170 L 110 200 L 30 150 Z" fill="#A1E1B9" fillOpacity="0.55" stroke="#242220" strokeWidth="1.5" />
      <path d="M240 130 L 330 100 L 420 140 L 430 240 L 360 290 L 270 270 L 230 210 Z" fill="#A1E1B9" fillOpacity="0.4" stroke="#242220" strokeWidth="1.5" />
      <path d="M60 250 L 200 230 L 240 310 L 150 340 L 50 320 Z" fill="#A1E1B9" fillOpacity="0.5" stroke="#242220" strokeWidth="1.5" />
      <path d="M80 130 Q 160 140, 200 200 T 320 220 Q 360 230, 400 200" stroke="#FF9456" strokeWidth="3" fill="none" strokeDasharray="10 6" strokeLinecap="round" />
      <path d="M100 290 Q 170 260, 230 270 T 360 250" stroke="#FBD556" strokeWidth="3" fill="none" strokeDasharray="2 6" strokeLinecap="round" />
      <g transform="translate(72 122)">
        <path d="M8 0 C 3 0, 0 4, 0 8 C 0 14, 8 22, 8 22 C 8 22, 16 14, 16 8 C 16 4, 13 0, 8 0 Z" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <circle cx="8" cy="8" r="3" fill="#242220" />
      </g>
      <g transform="translate(392 192)">
        <path d="M8 0 C 3 0, 0 4, 0 8 C 0 14, 8 22, 8 22 C 8 22, 16 14, 16 8 C 16 4, 13 0, 8 0 Z" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <circle cx="8" cy="8" r="3" fill="#242220" />
      </g>
      <g transform="translate(20 20)">
        <rect width="140" height="26" rx="13" fill="#1E1E1E" />
        <circle cx="14" cy="13" r="4" fill="#FF9456" />
        <text x="26" y="17" fontSize="11" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Ranger profile</text>
      </g>
      <g transform="translate(170 20)">
        <rect width="130" height="26" rx="13" fill="#1E1E1E" />
        <circle cx="14" cy="13" r="4" fill="#FBD556" />
        <text x="26" y="17" fontSize="11" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Hiker profile</text>
      </g>
      <g transform="translate(410 24)">
        <circle cx="14" cy="14" r="14" fill="#1E1E1E" stroke="#242220" strokeWidth="1.5" />
        <path d="M14 4 L 17 14 L 14 24 L 11 14 Z" fill="#FBD556" />
        <text x="14" y="2" textAnchor="middle" fontSize="6" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">N</text>
      </g>
    </svg>
  </div>
);

const PassIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#1E1E1E] border-[1.5px] border-[#FDF5EB]/15 p-6 overflow-hidden shadow-[8px_8px_0_0_#242220] flex items-center justify-center">
    <svg viewBox="0 0 480 360" className="absolute inset-0 w-full h-full" aria-hidden="true">
      {[{ x: 80, y: 70, s: 8 }, { x: 410, y: 90, s: 10 }, { x: 50, y: 280, s: 6 }, { x: 440, y: 270, s: 9 }, { x: 350, y: 50, s: 5 }].map((sp, i) => (
        <path key={i} d={`M${sp.x} ${sp.y - sp.s} L${sp.x + sp.s / 3} ${sp.y - sp.s / 3} L${sp.x + sp.s} ${sp.y} L${sp.x + sp.s / 3} ${sp.y + sp.s / 3} L${sp.x} ${sp.y + sp.s} L${sp.x - sp.s / 3} ${sp.y + sp.s / 3} L${sp.x - sp.s} ${sp.y} L${sp.x - sp.s / 3} ${sp.y - sp.s / 3} Z`} fill="#FF9456" opacity="0.8">
          <animate attributeName="opacity" values="0.3;1;0.3" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" />
        </path>
      ))}
    </svg>
    <div className="relative w-[200px] h-[300px] rounded-[28px] border-[8px] border-[#242220] bg-[#FDF5EB] p-4 z-10">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-3 bg-[#242220] rounded-b-xl" />
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#242220]/70 mb-1">Park Útlevél</div>
        <div className="text-base font-bold text-[#242220] mb-4 leading-tight">Aggtelek Trail<br />Card</div>
        <div className="flex items-center justify-between mb-5">
          {[true, true, false].map((filled, i) => (
            <div key={i} className="w-12 h-12 rounded-full border-[1.5px] border-[#242220] flex items-center justify-center" style={{ backgroundColor: filled ? "#FBD556" : "transparent" }}>
              {filled && (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 4 C 8 6, 7 10, 8 13 C 9 15, 12 16, 12 16 C 12 16, 15 15, 16 13 C 17 10, 16 6, 12 4 Z" fill="#242220" />
                  <path d="M12 8 V 16" stroke="#FBD556" strokeWidth="1" />
                </svg>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-[2px] mb-4">
          {[3, 1, 2, 1, 4, 1, 2, 3, 1, 2, 1, 3, 2, 1, 4, 1].map((w, i) => (
            <div key={i} className="bg-[#242220]" style={{ width: `${w}px`, height: "28px" }} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-3 h-3">
            <span className="absolute inset-0 rounded-full bg-[#A1E1B9] animate-ping opacity-75" />
            <span className="absolute inset-0.5 rounded-full bg-[#A1E1B9]" />
          </div>
          <span className="text-[10px] font-bold text-[#242220] uppercase tracking-wider">GPS Locked</span>
        </div>
      </div>
    </div>
  </div>
);

const IdentityVerificationIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="NFC identity verification diagram">
      <g stroke="#242220" strokeWidth="1.5" fill="none">
        <rect x="60" y="40" width="140" height="220" rx="16" fill="#FDF5EB" stroke="#242220" />
        <rect x="68" y="72" width="124" height="80" rx="6" fill="#1E1E1E" stroke="#242220" />
        <text x="130" y="120" textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">MRZ</text>
        <text x="130" y="140" textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">L0123456</text>
        <line x1="80" y1="170" x2="180" y2="170" stroke="#242220" strokeWidth="1" strokeDasharray="3 3" />
        <rect x="80" y="180" width="100" height="14" rx="4" fill="#A1E1B9" stroke="#242220" strokeWidth="1" />
        <text x="130" y="191" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">NFC CHIP PRESENT</text>
        <rect x="80" y="200" width="100" height="14" rx="4" fill="#FBD556" stroke="#242220" strokeWidth="1" />
        <text x="130" y="211" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">BIOMETRIC MATCH ✓</text>
        <rect x="80" y="220" width="100" height="14" rx="4" fill="#FF9456" stroke="#242220" strokeWidth="1" />
        <text x="130" y="231" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">NO WATCHLIST HITS</text>
        <path d="M40 130 L60 130" stroke="#242220" />
        <path d="M40 130 L34 124 M40 130 L34 136" stroke="#242220" strokeWidth="1.5" />
      </g>
      <g transform="translate(210 130)">
        <circle cx="30" cy="30" r="30" fill="#1E1E1E" stroke="#A1E1B9" strokeWidth="2" />
        <path d="M18 30 L24 36 L36 22" stroke="#A1E1B9" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="30" y="76" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">IDENTITY</text>
        <text x="30" y="88" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">VERIFIED</text>
      </g>
      <g transform="translate(280 160)">
        <animateTransform attributeName="transform" type="rotate" values="0 42 42;360 42 42" dur="4s" repeatCount="indefinite" />
        <circle cx="42" cy="42" r="36" fill="none" stroke="#242220" strokeWidth="1.5" strokeDasharray="6 8" />
        <text x="42" y="34" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">NFC</text>
        <text x="42" y="48" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">SCAN</text>
      </g>
      <g transform="translate(340 70)">
        <rect width="100" height="40" rx="8" fill="#242220" stroke="#242220" strokeWidth="1.5" />
        <text x="50" y="18" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">3-day auto-delete</text>
        <text x="50" y="32" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">GDPR compliant</text>
      </g>
      <g transform="translate(340 130)">
        <rect width="100" height="30" rx="8" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <text x="50" y="20" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">LEGAL FLAG ⚑</text>
      </g>
      <g transform="translate(20 280)">
        <rect width="440" height="56" rx="12" fill="#242220" />
        <text x="220" y="24" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">PII DATA FLOW</text>
        <text x="220" y="42" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.7">Scan → Extract → Cross-reference → Auto-delete (3 working days) → Legal flag if proceeding</text>
      </g>
    </svg>
  </div>
);

const AnimalRescueIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#1E1E1E] border-[1.5px] border-[#FDF5EB]/15 p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Animal rescue workflow">
      <g fill="none" stroke="#FDF5EB" strokeOpacity="0.06" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      <path d="M240 20 L 420 100 L 440 220 L 300 340 L 140 320 L 60 200 L 80 100 Z" fill="#FF9456" fillOpacity="0.08" stroke="#FF9456" strokeOpacity="0.3" strokeWidth="1" />
      {[
        { x: 60, y: 60, c: "#FBD556", label: "01 · Report", icon: "!" },
        { x: 170, y: 50, c: "#A1E1B9", label: "02 · Transfer", icon: "→" },
        { x: 290, y: 70, c: "#FBD556", label: "03 · Vet Care", icon: "+" },
        { x: 380, y: 140, c: "#A1E1B9", label: "04 · Release", icon: "✓" },
        { x: 380, y: 250, c: "#FF9456", label: "05 · Disposal", icon: "✕" },
      ].map((node) => (
        <g key={node.label} transform={`translate(${node.x} ${node.y})`}>
          <rect x="-56" y="-18" width="112" height="36" rx="18" fill={node.c} stroke="#242220" strokeWidth="1.5" />
          <text x="0" y="5" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">{node.label}</text>
        </g>
      ))}
      <path d="M116 60 L170 50" stroke="#FDF5EB" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrAR)" />
      <path d="M226 60 L290 70" stroke="#FDF5EB" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrAR)" />
      <path d="M340 90 Q 360 110, 380 140" stroke="#FDF5EB" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrAR)" />
      <path d="M380 180 V 250" stroke="#FDF5EB" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrAR)" />
      <defs><marker id="arrAR" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="4" markerHeight="4" orient="auto">
        <path d="M0,0 L8,5 L0,10 z" fill="#FDF5EB" />
      </marker></defs>
      <g transform="translate(20 220)">
        <rect width="180" height="100" rx="12" fill="#1E1E1E" stroke="#FBD556" strokeWidth="1.5" strokeOpacity="0.5" />
        <text x="90" y="24" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">CARE CASE</text>
        {[
          { l: "Vet procedures: 3", c: "#A1E1B9" },
          { l: "Feeding days: 14", c: "#FBD556" },
          { l: "Total cost: 42,500 Ft", c: "#FF9456" },
        ].map((r, i) => (
          <React.Fragment key={r.l}>
            <text x="16" y={46 + i * 18} fontSize="9" fontWeight="700" fontFamily="Quicksand" fill={r.c}>{r.l}</text>
          </React.Fragment>
        ))}
      </g>
      <g transform="translate(260 220)">
        <rect width="180" height="100" rx="12" fill="#1E1E1E" stroke="#A1E1B9" strokeWidth="1.5" strokeOpacity="0.5" />
        <text x="90" y="24" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">HANDOVER</text>
        <text x="16" y="46" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Ranger: NB</text>
        <text x="16" y="64" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Recipient: ANPI</text>
        <text x="16" y="82" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">Signed (biometric) ✓</text>
        <text x="140" y="82" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">PDF</text>
      </g>
      <g transform="translate(140 300)">
        <rect width="180" height="36" rx="18" fill="#242220" />
        <text x="90" y="22" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">GBIF species normalisation</text>
      </g>
    </svg>
  </div>
);

const FleetManagementIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Fleet and asset management">
      <g stroke="#242220" strokeOpacity="0.08" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      <g transform="translate(30 40)">
        <rect width="200" height="130" rx="12" fill="#FBD556" stroke="#242220" strokeWidth="1.5" />
        <text x="100" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">VEHICLE FLEET</text>
        <line x1="16" y1="32" x2="184" y2="32" stroke="#242220" strokeWidth="1" opacity="0.3" />
        {[{ l: "AB-1234 · Range Rover", m: "12,340 km / 15,000 km" }, { l: "CD-5678 · Toyota Hilux", m: "8,210 km / 15,000 km" }, { l: "EF-9012 · Suzuki Vitara", m: "15,678 km / 15,000 km" }].map((v, i) => (
          <g key={v.l}>
            <rect x="16" y={42 + i * 28} width="168" height="22" rx="6" fill="#242220" fillOpacity="0.08" />
            <text x="24" y={56 + i * 28} fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">{v.l}</text>
            <text x="120" y={56 + i * 28} fontSize="7" fontFamily="Quicksand" fill="#242220" opacity="0.6">{v.m}</text>
          </g>
        ))}
      </g>
      <g transform="translate(260 40)">
        <rect width="190" height="130" rx="12" fill="#A1E1B9" stroke="#242220" strokeWidth="1.5" />
        <text x="95" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">WAREHOUSE ASSETS</text>
        <line x1="16" y1="32" x2="174" y2="32" stroke="#242220" strokeWidth="1" opacity="0.3" />
        {[{ l: "Radio · ANPI-0001", s: "In use" }, { l: "GPS Unit · ANPI-0002", s: "In stock" }, { l: "Binoculars · ANPI-0003", s: "Maintenance" }].map((a, i) => (
          <g key={a.l}>
            <rect x="16" y={42 + i * 28} width="158" height="22" rx="6" fill="#242220" fillOpacity="0.08" />
            <text x="24" y={56 + i * 28} fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">{a.l}</text>
            <circle cx="160" cy={53 + i * 28} r="4" fill={i === 0 ? "#FBD556" : i === 1 ? "#A1E1B9" : "#FF9456"} />
          </g>
        ))}
      </g>
      <g transform="translate(30 190)">
        <rect width="200" height="90" rx="12" fill="#FDF5EB" stroke="#242220" strokeWidth="1.5" />
        <text x="100" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">WAYBILL · 2025-06-11</text>
        <text x="16" y="44" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">Start: Aggtelek HQ</text>
        <text x="16" y="60" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">End: Jósvafő Station</text>
        <text x="16" y="76" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FF9456">⏎ Odometer: 12,340 → 12,378 ✓</text>
      </g>
      <g transform="translate(260 190)">
        <rect width="190" height="90" rx="12" fill="#242220" />
        <text x="95" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">CAR DAMAGE DIAGRAM</text>
        <path d="M40 40 L150 40 L140 70 L50 70 Z" fill="#FDF5EB" stroke="#FF9456" strokeWidth="2" />
        <circle cx="110" cy="50" r="10" fill="none" stroke="#FF4D4D" strokeWidth="2" strokeDasharray="3 2" />
        <circle cx="110" cy="50" r="3" fill="#FF4D4D" />
        <text x="110" y="40" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#FF4D4D">DENT</text>
        <rect x="70" y="60" width="80" height="14" rx="4" fill="#FF9456" />
        <text x="110" y="71" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">REPAIR QUOTATION</text>
      </g>
      <g transform="translate(120 300)">
        <rect width="240" height="36" rx="18" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <text x="120" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">Digital signature on every transaction ✓</text>
      </g>
    </svg>
  </div>
);

const SurveyIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-6 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#242220]/60">Field Form · NPI-21</div>
        <div className="text-base font-bold text-[#242220]">Braun-Blanquet Relevé</div>
      </div>
      <div className="px-3 py-1 rounded-full bg-[#A1E1B9] text-[10px] font-bold text-[#242220] uppercase tracking-wider border-[1.5px] border-[#242220]">Natura 2000</div>
    </div>
    <div className="border-[1.5px] border-[#242220] rounded-xl overflow-hidden text-[12px] mb-4">
      <div className="grid grid-cols-[1.6fr_0.8fr_0.8fr] bg-[#242220] text-[#FDF5EB] font-bold uppercase tracking-wider text-[10px]">
        <div className="px-3 py-2">Species</div>
        <div className="px-3 py-2 border-l border-[#FDF5EB]/20">Cover</div>
        <div className="px-3 py-2 border-l border-[#FDF5EB]/20">Layer</div>
      </div>
      {[["Quercus petraea", "4", "A1"], ["Carpinus betulus", "3", "A2"], ["Anemone ranunculoides", "2b", "C"], ["Galium odoratum", "+", "C"]].map((row, i) => (
        <div key={i} className={`grid grid-cols-[1.6fr_0.8fr_0.8fr] text-[#242220] font-semibold ${i % 2 === 0 ? "bg-[#FDF5EB]" : "bg-[#FBD556]/20"}`}>
          <div className="px-3 py-1.5 italic">{row[0]}</div>
          <div className="px-3 py-1.5 border-l border-[#242220]/20">{row[1]}</div>
          <div className="px-3 py-1.5 border-l border-[#242220]/20">{row[2]}</div>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between text-[11px] font-bold text-[#242220]/70 font-mono">
      <span>48.4742°N · 20.5025°E</span>
      <span className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF9456]" />
        EOV → WGS84
      </span>
    </div>
  </div>
);

const OnSiteFinesIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="On-site fines with Bluetooth printer">
      <g stroke="#242220" strokeOpacity="0.06" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      <g transform="translate(20 20)">
        <rect width="160" height="240" rx="16" fill="#1E1E1E" stroke="#242220" strokeWidth="2" />
        <rect x="8" y="8" width="144" height="224" rx="12" fill="#1E1E1E" />
        <rect x="50" y="2" width="60" height="4" rx="2" fill="#242220" />
        <rect x="12" y="20" width="136" height="24" rx="6" fill="#242220" />
        <text x="80" y="36" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">HELYSZÍNI BÍRSÁG</text>
        <rect x="12" y="50" width="136" height="22" rx="4" fill="#FF9456" />
        <text x="80" y="65" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">TV_TILOS_BEHAJTAS</text>
        <text x="80" y="82" textAnchor="middle" fontSize="7" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.7">5,000 - 50,000 HUF</text>
        <line x1="12" y1="94" x2="148" y2="94" stroke="#FDF5EB" strokeWidth="1" opacity="0.15" />
        <text x="16" y="112" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Elkövető</text>
        <text x="16" y="126" fontSize="7" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">Nagy János · 1985.03.14</text>
        <text x="16" y="140" fontSize="7" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">ANYJA: Kovács Mária</text>
        <rect x="12" y="152" width="100" height="16" rx="4" fill="#A1E1B9" />
        <text x="62" y="164" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">Prior offenses: 1</text>
        <rect x="12" y="176" width="136" height="28" rx="6" fill="#242220" stroke="#FBD556" strokeWidth="1" />
        <text x="80" y="190" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">AMOUNT: 25,000 Ft</text>
        <text x="80" y="202" textAnchor="middle" fontSize="7" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.5">Fiz. határidő: 30 nap</text>
        <rect x="12" y="212" width="64" height="20" rx="4" fill="#FBD556" />
        <text x="44" y="226" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">Aláírás ✓</text>
        <rect x="84" y="212" width="64" height="20" rx="4" fill="#FF9456" />
        <text x="116" y="226" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">Nyomtatás</text>
      </g>
      <g transform="translate(200 160)">
        <rect width="140" height="100" rx="16" fill="#242220" />
        <rect x="8" y="8" width="124" height="84" rx="10" fill="#1E1E1E" />
        <rect x="30" y="-4" width="80" height="6" rx="3" fill="#FDF5EB" />
        <text x="70" y="30" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Bluetooth Printer</text>
        <text x="70" y="44" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">⚡ CONNECTED</text>
        <rect x="20" y="56" width="100" height="24" rx="4" fill="#242220" stroke="#FBD556" strokeWidth="1" />
        <text x="70" y="72" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">58mm · Thermal</text>
      </g>
      <g transform="translate(350 40)">
        <rect width="110" height="160" rx="6" fill="#FDF5EB" stroke="#242220" strokeWidth="1.5" />
        <text x="55" y="20" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#242220">HELYSZÍNI BÍRSÁG</text>
        <text x="55" y="32" textAnchor="middle" fontSize="6" fontFamily="Quicksand" fill="#242220" opacity="0.6">ANPI-2026-000042</text>
        <line x1="8" y1="38" x2="102" y2="38" stroke="#242220" strokeWidth="1" opacity="0.3" />
        <text x="12" y="52" fontSize="6" fontWeight="700" fontFamily="Quicksand" fill="#242220">Nagy János</text>
        <text x="12" y="62" fontSize="6" fontFamily="Quicksand" fill="#242220" opacity="0.6">25,000 Ft</text>
        <text x="12" y="74" fontSize="6" fontFamily="Quicksand" fill="#242220" opacity="0.6">IBAN: HU12 3456 ...</text>
        <rect x="20" y="84" width="70" height="70" rx="4" fill="#FDF5EB" stroke="#242220" strokeWidth="1" />
        <g transform="translate(34 98)">
          {[0, 1, 2].map((row) =>
            Array.from({ length: 3 }).map((_, col) => (
              <rect key={`${row}-${col}`} x={col * 18} y={row * 18} width="14" height="14" rx="2" fill="#242220" opacity={0.15 + ((row * 3 + col) % 4) * 0.15} />
            ))
          )}
        </g>
      </g>
      <g transform="translate(260 60)">
        <circle cx="10" cy="10" r="10" fill="#A1E1B9" stroke="#242220" strokeWidth="1" />
        <path d="M5 10 L8 13 L15 7" stroke="#242220" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <path d="M180 170 L200 170" stroke="#242220" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrOSF)" />
      <defs><marker id="arrOSF" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="4" markerHeight="4" orient="auto">
        <path d="M0,0 L8,5 L0,10 z" fill="#242220" />
      </marker></defs>
      <g transform="translate(230 270)">
        <rect width="210" height="32" rx="16" fill="#242220" stroke="#FBD556" strokeWidth="1.5" />
        <circle cx="22" cy="16" r="8" fill="#FBD556" />
        <path d="M14 16 L19 20 L26 12" stroke="#242220" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="38" y="21" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">NFC card payment · no POS terminal needed</text>
      </g>
      <g transform="translate(20 300)">
        <rect width="440" height="36" rx="18" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <text x="220" y="23" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">100% Magyar jogi megfelelés · BM rendelet szerint</text>
      </g>
    </svg>
  </div>
);

const CropComplianceIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="AKG and Natura 2000 compliance checks">
      <g stroke="#242220" strokeOpacity="0.06" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      <g transform="translate(20 20)">
        <rect width="180" height="120" rx="10" fill="#A1E1B9" fillOpacity="0.3" stroke="#242220" strokeWidth="1.5" />
        <text x="90" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">PARCEL MAP</text>
        <path d="M20 60 L70 40 L120 55 L140 90 L80 110 L30 100 Z" fill="#A1E1B9" stroke="#242220" strokeWidth="1.5" fillOpacity="0.5" />
        <text x="80" y="80" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#242220">HRSZ: 042/5</text>
        <text x="80" y="94" textAnchor="middle" fontSize="7" fontFamily="Quicksand" fill="#242220" opacity="0.7">2.4 ha · Natura 2000</text>
        <g transform="translate(130 10)">
          <rect width="44" height="16" rx="4" fill="#FBD556" />
          <text x="22" y="12" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#242220">T: ✓</text>
        </g>
        <g transform="translate(130 30)">
          <rect width="44" height="16" rx="4" fill="#A1E1B9" />
          <text x="22" y="12" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#242220">U: ✗</text>
        </g>
        <g transform="translate(130 50)">
          <rect width="44" height="16" rx="4" fill="#FF9456" />
          <text x="22" y="12" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#242220">V: ✓</text>
        </g>
      </g>
      <g transform="translate(220 20)">
        <rect width="240" height="120" rx="10" fill="#1E1E1E" stroke="#FBD556" strokeWidth="1.5" />
        <text x="120" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">AI ANALYSIS</text>
        <rect x="12" y="32" width="66" height="66" rx="6" fill="#242220" stroke="#FDF5EB" strokeWidth="1" strokeOpacity="0.2" />
        <rect x="16" y="36" width="58" height="58" rx="4" fill="#FDF5EB" fillOpacity="0.1" />
        {Array.from({ length: 3 }).map((_, row) =>
          Array.from({ length: 3 }).map((_, col) => (
            <rect key={`${row}-${col}`} x={16 + col * 18} y={36 + row * 18} width="14" height="14" rx="2" fill="#A1E1B9" opacity={0.15 + ((row * 3 + col) % 4) * 0.2} />
          ))
        )}
        <circle cx="46" cy="59" r="4" fill="#FBD556" />
        <text x="88" y="48" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Vegetation: Medium</text>
        <text x="88" y="64" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">Mowed: Yes ✓</text>
        <text x="88" y="80" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FF9456">Invasive: None detected</text>
        <rect x="12" y="98" width="216" height="14" rx="4" fill="#242220" stroke="#FBD556" strokeWidth="1" />
        <text x="120" y="110" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">Confidence: High · Gemini Vision</text>
      </g>
      <g transform="translate(20 160)">
        <rect width="200" height="120" rx="10" fill="#FDF5EB" stroke="#242220" strokeWidth="1.5" />
        <text x="100" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">QUESTIONNAIRE · nattabla_qa</text>
        <line x1="12" y1="30" x2="188" y2="30" stroke="#242220" strokeWidth="1" opacity="0.3" />
        {[
          { q: "Kaszálás megtörtént?", a: "Igen", c: "#A1E1B9" },
          { q: "Legeltetés nyomai?", a: "Nem", c: "#FF9456" },
          { q: "Cserjésedés jelei?", a: "NR", c: "#FBD556" },
        ].map((row, i) => (
          <g key={i}>
            <text x="16" y={48 + i * 24} fontSize="8" fontFamily="Quicksand" fill="#242220">{row.q}</text>
            <rect x="140" y={38 + i * 24} width="44" height="16" rx="4" fill={row.c} />
            <text x="162" y={50 + i * 24} textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#242220">{row.a}</text>
          </g>
        ))}
        <rect x="12" y="100" width="176" height="14" rx="4" fill="#242220" />
        <text x="100" y="112" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Predefined remark: Megfelelő állapotú</text>
      </g>
      <g transform="translate(240 160)">
        <rect width="220" height="120" rx="10" fill="#242220" />
        <text x="110" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">TECS PROGRAMMES (AKG)</text>
        <line x1="12" y1="30" x2="208" y2="30" stroke="#FBD556" strokeWidth="1" opacity="0.3" />
        {[
          { p: "MTÉT túzok szántó", s: "Active" },
          { p: "Talajmegújító", s: "Active" },
        ].map((prog, i) => (
          <g key={i}>
            <rect x="12" y={40 + i * 28} width="196" height="22" rx="6" fill="#FDF5EB" fillOpacity="0.08" />
            <text x="22" y={56 + i * 28} fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">{prog.p}</text>
            <circle cx="188" cy={52 + i * 28} r="4" fill="#A1E1B9" />
          </g>
        ))}
        <rect x="12" y="100" width="196" height="14" rx="4" fill="#FF9456" />
        <text x="110" y="112" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#242220">PDF report · Államkincstár-ready</text>
      </g>
      <g transform="translate(120 300)">
        <rect width="240" height="36" rx="18" fill="#FBD556" stroke="#242220" strokeWidth="1.5" />
        <text x="120" y="23" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">Magyar Államkincstár · hivatalos ellenőrzési rendszer</text>
      </g>
    </svg>
  </div>
);

const FeatureRow = ({ feature, index }) => {
  const reverse = index % 2 === 1;
  const Illustration = feature.Illustration;
  return (
    <section id={feature.id} data-testid={`showcase-${feature.id}`} className="border-t-[1.5px] border-[#242220]/80">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 lg:py-28">
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
          <div className="reveal">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#FF9456] mb-4">{feature.eyebrow}</div>
            <h3 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight text-[#FDF5EB] leading-[1.1] mb-4" data-testid={`showcase-${feature.id}-headline`}>{feature.headline}</h3>
            <div className="text-base sm:text-lg font-semibold text-[#FF9456] mb-6 leading-snug">{feature.sub}</div>
            <p className="text-[15px] text-[#FDF5EB]/70 leading-[1.75] mb-8 max-w-xl">{feature.body}</p>
            <ul className="space-y-3" aria-label={`${feature.headline} highlights`}>
              {feature.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span aria-hidden="true" className="mt-0.5 w-5 h-5 rounded-full bg-[#A1E1B9]/15 border border-[#A1E1B9]/40 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[#A1E1B9]" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-[#FDF5EB]/85 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal reveal-delay-2 flex justify-center lg:justify-end" data-testid={`showcase-${feature.id}-illustration`}>
            <Illustration />
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureShowcase = ({ lang }) => {
  const illustrations = [PipelineIllustration, BirdVoiceIllustration, ChainIllustration, RoutingIllustration, PassIllustration, SurveyIllustration, IdentityVerificationIllustration, AnimalRescueIllustration, FleetManagementIllustration, OnSiteFinesIllustration, CropComplianceIllustration];
  const featureKeys = ["cameraPipeline", "birdVoice", "aiChain", "routing", "parkPass", "ecology", "identityVerification", "animalRescue", "fleetManagement", "onSiteFines", "cropCompliance"];
  const featureIds = ["wildlife-camera", "bird-voice", "ai-chain", "routing", "park-pass", "ecology", "identity-verification", "animal-rescue", "fleet-management", "on-site-fines", "crop-compliance"];

  return (
    <div data-testid="section-showcase" className="bg-[#1E1E1E]">
      {featureKeys.map((key, i) => (
        <FeatureRow
          key={key}
          feature={{
            id: featureIds[i],
            ...t(lang, `showcase.${key}`),
            Illustration: illustrations[i],
          }}
          index={i}
        />
      ))}
    </div>
  );
};

export default FeatureShowcase;
